/**
 * SlippageSettings — preset buttons (0.5%, 1%, 3%) and custom input.
 *
 * Default: 0.5% (50 bps). Persists to localStorage.
 * Warning when > 5%.
 *
 * Story 5.1 Task 6.2
 */

import { useCallback, useEffect, useState } from "react";

const PRESETS = [
	{ label: "0.5%", bps: 50 },
	{ label: "1%", bps: 100 },
	{ label: "3%", bps: 300 },
] as const;

const STORAGE_KEY = "swap-slippage-bps";
const HIGH_SLIPPAGE_THRESHOLD_BPS = 500; // 5%

type SlippageSettingsProps = {
	slippageBps: number;
	onSlippageChange: (bps: number) => void;
};

export function SlippageSettings({ slippageBps, onSlippageChange }: SlippageSettingsProps) {
	const [customValue, setCustomValue] = useState("");
	const isPreset = PRESETS.some((p) => p.bps === slippageBps);
	const showWarning = slippageBps > HIGH_SLIPPAGE_THRESHOLD_BPS;

	// Load from localStorage on mount
	useEffect(() => {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			const parsed = Number(stored);
			if (!Number.isNaN(parsed) && parsed > 0) {
				onSlippageChange(parsed);
				if (!PRESETS.some((p) => p.bps === parsed)) {
					setCustomValue(String(parsed / 100));
				}
			}
		}
	}, [onSlippageChange]);

	// Persist to localStorage
	useEffect(() => {
		localStorage.setItem(STORAGE_KEY, String(slippageBps));
	}, [slippageBps]);

	const handlePresetClick = useCallback(
		(bps: number) => {
			setCustomValue("");
			onSlippageChange(bps);
		},
		[onSlippageChange]
	);

	const handleCustomChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value;
			// Allow only valid numeric input
			if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;

			setCustomValue(raw);

			if (raw === "") return;

			const parsed = Number.parseFloat(raw);
			if (!Number.isNaN(parsed) && parsed > 0 && parsed <= 50) {
				const bps = Math.round(parsed * 100);
				onSlippageChange(bps);
			}
		},
		[onSlippageChange]
	);

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2">
				<span className="text-xs text-pastel-gray font-medium">Slippage</span>
				<div className="flex gap-1" aria-label="Slippage tolerance">
					{PRESETS.map((preset) => (
						<button
							key={preset.bps}
							type="button"
							aria-pressed={slippageBps === preset.bps && isPreset}
							aria-label={preset.label}
							onClick={() => handlePresetClick(preset.bps)}
							className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
								slippageBps === preset.bps && isPreset
									? "bg-pastel-vibrant-blue text-white"
									: "bg-pastel-bg text-pastel-gray hover:bg-pastel-border"
							}`}
						>
							{preset.label}
						</button>
					))}
				</div>
				<div className="relative flex-1 max-w-[80px]">
					<input
						type="text"
						inputMode="decimal"
						placeholder="Custom"
						value={customValue}
						onChange={handleCustomChange}
						aria-label="Custom slippage percentage"
						className="w-full rounded-md border border-pastel-border bg-white px-2 py-1 text-xs text-pastel-black text-right pr-5 focus:border-pastel-vibrant-blue focus:outline-none"
					/>
					<span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-pastel-gray">
						%
					</span>
				</div>
			</div>

			{showWarning && (
				<div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-700">
					High slippage — you may receive significantly less
				</div>
			)}
		</div>
	);
}
